<template>
  <div
    :class="[
      'glass-tile flex items-center gap-3 rounded-xl p-3 transition-opacity',
      status === 'error' && 'border-destructive/50!',
      status === 'warning' && 'border-warning/60!',
      isReconnecting && 'opacity-60',
    ]"
  >
    <img :src="entity.picture" alt="" class="size-10 shrink-0 rounded-full object-cover">

    <div class="flex min-w-0 flex-1 flex-col">
      <span class="truncate text-sm font-medium text-foreground">{{ entity.name }}</span>

      <span v-if="hasIntervention" class="flex items-center gap-1 text-xs font-semibold text-destructive">
        {{ tr('Intervention required') }}
        <button
          v-tooltip="tr('Click for instructions')"
          type="button"
          data-slot="intervention-info"
          class="inline-flex cursor-pointer text-destructive"
          :aria-label="tr('Click for instructions')"
          @click.stop="() => openInterventionGuide()"
        >
          <info-icon class="size-3.5" />
        </button>
      </span>
      <span
        v-else-if="statusText"
        :class="[ 'text-xs font-semibold', status === 'error' ? 'text-destructive' : status === 'warning' ? 'text-warning' : 'text-muted-foreground' ]"
      >
        {{ statusText }}
      </span>
    </div>

    <div class="flex shrink-0 items-center">
      <ui-button
        v-if="entity.owner.id === $root.loggedInUser.id && !$root.isSupportMode"
        v-tooltip="tr('Reconnect')"
        variant="ghost"
        size="icon"
        :aria-label="tr('Reconnect')"
        @click="() => reconnectProfile()"
      >
        <refresh-cw-icon :class="isReconnecting && 'animate-spin'" />
      </ui-button>
      <ui-button
        v-if="disconnectButtonVisible && entity.owner.id === $root.loggedInUser.id"
        v-tooltip="tr('Disconnect')"
        variant="ghost"
        size="icon"
        :aria-label="tr('Disconnect')"
        @click="() => removeProfile()"
      >
        <unplug-icon />
      </ui-button>
      <ui-button
        v-if="$root.supportHasL2Access"
        v-tooltip="tr('Debug')"
        variant="ghost"
        size="icon"
        :aria-label="tr('Debug')"
        @click="() => openCommunicatorDebug(entity)"
      >
        <bug-icon />
      </ui-button>
    </div>
  </div>
</template>

<script>
import { Bug, Info, RefreshCw, Unplug } from '@lucide/vue';

import ConnectedProfile from '@/entities/ConnectedProfile.js';
import CommunicatorDebug from '@/ui/components/CommunicatorDebug.vue';
import InterventionGuideModal from '@/ui/components/InterventionGuideModal.vue';

export default {
  name: 'ConnectedProfileItem',
  components: {
    BugIcon: Bug,
    InfoIcon: Info,
    RefreshCwIcon: RefreshCw,
    UnplugIcon: Unplug,
  },
  props: {
    entity: {
      type: ConnectedProfile,
      required: true,
    },
  },
  data: () => ({
    isReconnecting: false,
  }),
  computed: {
    hasIntervention() {
      return !!this.entity.intervention_code;
    },
    status() {
      if (this.isExpired || this.hasIntervention) {
        return 'error';
      }

      return this.hasMissingPermission || this.willBeExpiredWarning ? 'warning' : null;
    },
    statusText() {
      if (!this.entity.token_expire_at) {
        return this.hasMissingPermission ? this.tr('Missing permission.') + ' ' + this.tr('Reconnection is required!') : '';
      }
      if (this.isExpired) {
        return this.tr('Reconnection is required!');
      }
      if (this.hasMissingPermission) {
        return this.tr('Missing permission.') + ' ' + this.tr('Reconnection is required!');
      }

      return this.tr('Expires in :time', { ':time': this.expireIn });
    },
    isConnecting() {
      return this.$store.getters['app/isConnecting'];
    },
    expireInSeconds() {
      if (this.entity.token_expire_at === null) {
        return null;
      }

      return Math.floor((this.entity.token_expire_at.getTime() - (new Date()).getTime()) / 1000);
    },
    hasMissingPermission() {
      return this.entity.missing_scope;
    },
    isExpired() {
      return this.expireInSeconds !== null && this.expireInSeconds <= 0;
    },
    willBeExpiredWarning() {
      return this.expireInSeconds > 0 && this.expireInSeconds < 86400 * 7;
    },
    expireIn() {
      const inDays = Math.floor(this.expireInSeconds / 86400);
      if (inDays >= 2) {
        return this.tr(':num days', { ':num': inDays });
      }

      const inHours = Math.floor(this.expireInSeconds / 3600);
      if (inHours >= 2) {
        return this.tr(':num hours', { ':num': inHours });
      }

      return this.tr(':num minutes', { ':num': Math.floor(this.expireInSeconds / 60) });
    },
    disconnectButtonVisible() {
      return this.$root.isInOriginalCompany && !this.$root.isSupportMode;
    },
  },
  watch: {
    isConnecting(val) {
      if (!val) {
        this.isReconnecting = false;
      }
    },
  },
  methods: {
    removeProfile() {
      if (this.isConnecting) {
        return;
      }

      this.$emit('remove');
    },
    reconnectProfile() {
      if (this.isConnecting) {
        return;
      }

      this.isReconnecting = true;
      this.$emit('reconnect');
    },
    openInterventionGuide() {
      this.$fullscreenModal.open(
        this,
        InterventionGuideModal,
        {
          interventionCode: this.entity.intervention_code,
        },
      );
    },
    openCommunicatorDebug(connectedProfile) {
      this.$fullscreenModal.open(
        this,
        CommunicatorDebug,
        {
          entity: connectedProfile,
        },
        { wide: true },
      );
    },
  },
};
</script>
